import { ArrowLeft, CalendarDays, Table2 } from 'lucide-react'
import { Link } from '@tanstack/react-router'

const weekDays = [
  { label: '周一', day: '29' },
  { label: '周二', day: '30' },
  { label: '周三', day: '01', today: true },
  { label: '周四', day: '02' },
  { label: '周五', day: '03' },
  { label: '周六', day: '04' },
  { label: '周日', day: '05' },
]

const hours = Array.from({ length: 24 }, (_, i) => `${String(i + 1).padStart(2, '0')}:00`)

export function CalendarCanvas() {
  return (
    <div className="flex h-screen flex-col overflow-hidden bg-background">
      {/* header */}
      <header className="sticky top-0 flex shrink-0 items-center gap-2.5 p-1.5 px-6 pl-6">
        <Link
          to="/app"
          className="rounded-xl p-2.5 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
        >
          <ArrowLeft className="size-4" />
        </Link>
        <div className="ml-3 flex min-w-0 items-center gap-2.5">
          <span className="text-base font-semibold text-foreground">日历</span>
          <div className="flex shrink-0 items-center gap-x-1 rounded-md bg-card px-2.5 py-1.5 text-muted-foreground">
            <span className="text-sm font-medium leading-tight">GMT+8</span>
          </div>
        </div>
        <div className="grow" />
        <button className="inline-flex h-8 items-center justify-center gap-2 rounded-full bg-primary px-4 text-xs font-medium text-primary-foreground transition-colors hover:bg-primary/90">
          安排发布
        </button>
      </header>

      {/* content grid */}
      <div className="grid flex-1 min-h-0 gap-3 px-3 pb-3 xl:grid-cols-[224px_minmax(0,1fr)]">
        {/* left: mini month calendar */}
        <div className="hidden min-h-0 flex-col overflow-hidden rounded-2xl border border-border bg-card xl:flex">
          <div className="min-h-0 flex-1 divide-y divide-border overflow-y-auto p-3">
            <div className="mb-2 flex items-center justify-between">
              <span className="text-[11px] font-semibold text-foreground">2026年7月</span>
              <div className="flex items-center gap-0.5 text-muted-foreground">
                <button className="rounded p-0.5 hover:bg-accent hover:text-foreground">‹</button>
                <button className="rounded px-1.5 py-0.5 text-[9px] font-medium hover:bg-accent hover:text-foreground">今天</button>
                <button className="rounded p-0.5 hover:bg-accent hover:text-foreground">›</button>
              </div>
            </div>
            <div className="mb-0.5 grid grid-cols-7">
              {['日', '一', '二', '三', '四', '五', '六'].map((d, i) => (
                <div key={i} className="py-0.5 text-center text-[9px] font-medium text-muted-foreground">{d}</div>
              ))}
            </div>
            <div className="grid grid-cols-7">
              {Array.from({ length: 35 }, (_, i) => String(i - 2)).map((day, i) => (
                <button
                  key={i}
                  className={`relative flex h-6 items-center justify-center rounded-md text-[10px] transition-colors ${
                    day === '1' ? 'bg-primary/20 font-semibold text-primary' : 'text-muted-foreground hover:bg-accent'
                  }`}
                >
                  {Number(day) > 0 && Number(day) <= 31 ? day : ''}
                </button>
              ))}
            </div>
            <span className="mt-2 block text-[11px] text-muted-foreground">本月暂无安排的发布</span>
          </div>
        </div>

        {/* right: week view */}
        <div className="flex min-h-0 flex-col overflow-hidden rounded-2xl border border-border bg-card">
          <div className="flex items-center justify-between border-b border-border px-3 py-3">
            <div className="ml-auto flex items-center gap-3">
              <div className="inline-flex items-center rounded-lg border border-border bg-card p-0.5">
                <button className="flex items-center gap-1.5 rounded-md bg-accent px-3 py-1.5 text-sm font-medium text-foreground">
                  <Table2 className="size-4" />
                  <span className="hidden sm:inline">周</span>
                </button>
                <button className="flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium text-muted-foreground hover:text-foreground">
                  <CalendarDays className="size-4" />
                  <span className="hidden sm:inline">月</span>
                </button>
              </div>
            </div>
          </div>
          <div className="min-h-0 flex-1 overflow-auto p-3">
            <div className="flex h-[calc(100vh-200px)] flex-col overflow-hidden rounded-xl border border-border">
              {/* date range header */}
              <div className="flex items-center gap-1 border-b border-border px-3 py-2">
                <span className="mr-2 text-base font-semibold text-foreground">
                  6月29日 – 7月5日, 2026
                </span>
                <button className="text-muted-foreground hover:text-foreground">‹</button>
                <button className="text-muted-foreground hover:text-foreground">›</button>
              </div>
              {/* scrollable grid */}
              <div className="min-h-0 flex-1 overflow-auto">
                <div className="min-w-[640px]">
                  {/* day header row */}
                  <div
                    className="sticky top-0 z-20 grid border-b border-border bg-card"
                    style={{ gridTemplateColumns: '64px repeat(7, minmax(0, 1fr))' }}
                  >
                    <div className="sticky left-0 z-30 border-r border-border bg-card pb-1 pr-1.5 text-right text-[10px] text-muted-foreground">
                      GMT+8
                    </div>
                    {weekDays.map((d) => (
                      <div key={d.day} className="flex snap-start items-center gap-2 border-l border-border px-3 py-2.5">
                        <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{d.label}</span>
                        <span className={`inline-flex h-6 min-w-6 items-center justify-center rounded-md px-1.5 text-sm font-semibold ${d.today ? 'bg-primary text-primary-foreground' : 'text-foreground'}`}>
                          {d.day}
                        </span>
                      </div>
                    ))}
                  </div>
                  {/* body grid */}
                  <div className="grid" style={{ gridTemplateColumns: '64px repeat(7, minmax(0, 1fr))' }}>
                    {/* time column */}
                    <div className="sticky left-0 z-20 border-r border-border bg-card">
                      {hours.map((h) => (
                        <div key={h} className="relative" style={{ height: '48px' }}>
                          <span className="absolute -top-2 right-1.5 text-[10px] text-muted-foreground">{h}</span>
                        </div>
                      ))}
                    </div>
                    {/* day columns */}
                    {weekDays.map((d) => (
                      <div key={d.day} className="relative z-10 border-l border-border">
                        {hours.map((h) => (
                          <div key={h} className="relative border-t border-border" style={{ height: '48px' }} />
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
